// Feed-only enhancements: keep Quill's existing link editor, formats and undo.
export function findWebLinks(text) {
  const links = [];
  const pattern = /(^|[\s([{\uFFFC])((?:https?:\/\/|www\.)[^\s<>"\uFFFC]+)/gi;
  for (const match of text.matchAll(pattern)) {
    let label = match[2].replace(/[.,!?:;]+$/, '');
    for (const [open, close] of [
      ['(', ')'],
      ['[', ']'],
      ['{', '}'],
    ]) {
      while (label.endsWith(close) && label.split(close).length > label.split(open).length) {
        label = label.slice(0, -1);
      }
    }
    try {
      const href = /^www\./i.test(label) ? `https://${label}` : label;
      const url = new URL(href);
      if (
        !['http:', 'https:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        !url.hostname.includes('.')
      )
        continue;
      links.push({ index: match.index + match[1].length, length: label.length, href });
    } catch {
      /* Incomplete URLs are left as editable text. */
    }
  }
  return links;
}

function linkAt(quill, index) {
  return index < 0 ? undefined : quill.getContents(index, 1).ops[0]?.attributes?.link;
}

export function selectedFeedLink(quill, range) {
  if (!range?.length) return null;
  const ops = quill.getContents(range.index, range.length).ops;
  const href = ops[0]?.attributes?.link;
  if (href && ops.every((op) => typeof op.insert === 'string' && op.attributes?.link === href)) {
    // Partial selection, or a selection crossing multiple links, must not open.
    if (
      linkAt(quill, range.index - 1) === href ||
      linkAt(quill, range.index + range.length) === href
    )
      return null;
    return href;
  }
  if (ops.some((op) => op.attributes?.link || typeof op.insert !== 'string')) return null;
  const text = quill
    .getContents()
    .ops.map((op) => (typeof op.insert === 'string' ? op.insert : '\uFFFC'))
    .join('');
  const link = findWebLinks(text).find(
    (candidate) => candidate.index === range.index && candidate.length === range.length,
  );
  return link?.href || null;
}

export function installFeedLinks(quill) {
  let formatting = false;
  let selecting = false;
  let composing = false;
  let selectionTimer;
  let linkTimer;
  let pending = [];
  const doc = quill.root.ownerDocument;

  const autoLink = () => {
    clearTimeout(linkTimer);
    const ranges = pending;
    pending = [];
    if (composing || !ranges.length) return;
    // getText omits embeds; use a single placeholder to preserve Delta indices.
    const text = quill
      .getContents()
      .ops.map((op) => (typeof op.insert === 'string' ? op.insert : '\uFFFC'))
      .join('');
    formatting = true;
    try {
      for (const link of findWebLinks(text)) {
        if (
          !ranges.some(
            (range) => range.start <= link.index + link.length && range.end >= link.index,
          )
        )
          continue;
        const ops = quill.getContents(link.index, link.length).ops;
        // Preserve manually authored destinations and explicit formatting.
        if (
          ops.some(
            (op) => op.attributes?.link || op.attributes?.code || typeof op.insert !== 'string',
          )
        )
          continue;
        quill.formatText(link.index, link.length, 'link', link.href, 'user');
      }
    } finally {
      formatting = false;
    }
  };
  const onText = (delta, old, source) => {
    if (source !== 'user' || formatting || composing) return;
    let index = 0;
    for (const op of delta.ops) {
      if (op.retain) index += op.retain;
      if (typeof op.insert === 'string') {
        // Pasted URLs link immediately; typed URLs finish on space or Enter.
        if (op.insert.length > 1 || /\s/.test(op.insert))
          pending.push({ start: index, end: index + op.insert.length });
        index += op.insert.length;
      } else if (op.insert) index += 1;
    }
    if (pending.length) {
      clearTimeout(linkTimer);
      linkTimer = setTimeout(autoLink, 0);
    }
  };
  const openSelectedLink = () => {
    if (selecting || composing || !quill.hasFocus()) return;
    const range = quill.getSelection();
    const href = selectedFeedLink(quill, range);
    if (!href) return;
    const tooltip = quill.theme?.tooltip;
    if (!tooltip?.edit) return;
    // Same UI and save/remove behavior as the toolbar's hyperlink action.
    tooltip.linkRange = { index: range.index, length: range.length };
    tooltip.edit('link', href);
  };
  const scheduleSelection = () => {
    clearTimeout(selectionTimer);
    selectionTimer = setTimeout(openSelectedLink, 0);
  };
  const onSelection = (range, old, source) => {
    if (source === 'user' && range?.length) scheduleSelection();
  };
  const onPointerDown = () => {
    selecting = true;
  };
  const onPointerUp = () => {
    if (selecting) {
      selecting = false;
      scheduleSelection();
    }
  };
  const onCompositionStart = () => {
    composing = true;
  };
  const onCompositionEnd = () => {
    composing = false;
  };
  quill.on('text-change', onText);
  quill.on('selection-change', onSelection);
  quill.root.addEventListener('pointerdown', onPointerDown);
  doc.addEventListener('pointerup', onPointerUp);
  doc.addEventListener('pointercancel', onPointerUp);
  quill.root.addEventListener('compositionstart', onCompositionStart);
  quill.root.addEventListener('compositionend', onCompositionEnd);
  return () => {
    clearTimeout(linkTimer);
    clearTimeout(selectionTimer);
    quill.off('text-change', onText);
    quill.off('selection-change', onSelection);
    quill.root.removeEventListener('pointerdown', onPointerDown);
    doc.removeEventListener('pointerup', onPointerUp);
    doc.removeEventListener('pointercancel', onPointerUp);
    quill.root.removeEventListener('compositionstart', onCompositionStart);
    quill.root.removeEventListener('compositionend', onCompositionEnd);
  };
}
